import { datadogRum } from '@datadog/browser-rum';
import { useAtom, useSetAtom } from 'jotai';
import isEqual from 'lodash/isEqual';
import { useCallback } from 'react';

import { useInsightsService } from '@/src/infrastructure/Protocol/Insights/useInsightsService';
import {
  atomChannelTotals,
  atomChannelTracking,
  atomMetricGraph,
  atomMetricType,
  atomShippingFilters,
  atomShippingMetricLoading,
} from '@/src/modules/InsightsModule/atoms/InsightsAtom';
import {
  InsightsMetricActionsId,
  InsightsMetricActionsType,
} from '@/src/modules/InsightsModule/interfaces/ContactsSummary';

import { ChannelTotalType, ShippingFilters } from '@/modules/InsightsModule/interfaces/Channel';

const calculateDaysDifference = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

export const useShippingMetric = () => {
  const service = useInsightsService();

  const [filters, setFilters] = useAtom(atomShippingFilters);
  const [loading, setLoading] = useAtom(atomShippingMetricLoading);

  const setChannelTracking = useSetAtom(atomChannelTracking);
  const setChannelTotals = useSetAtom(atomChannelTotals);
  const setMetricType = useSetAtom(atomMetricType);
  const setMetricGraph = useSetAtom(atomMetricGraph);

  const getData = useCallback(
    async (options?: ShippingFilters) => {
      setLoading(true);

      const filtersUpdated = !isEqual(options, filters);

      const response = await service.getActionsMetrics(options ?? filters);
      const { metrics } = response ?? { metrics: { channels: null, tracking: null } };

      setChannelTracking(metrics?.tracking ?? null);
      setChannelTotals(metrics?.channels ?? null);
      setMetricGraph(response?.history ?? []);

      if (filtersUpdated) setFilters(options ?? filters);

      const filterType = options?.filterType ?? filters.filterType;
      const startDate = options?.startDate ?? filters.startDate;
      const endDate = options?.endDate ?? filters.endDate;
      const daysDifference = calculateDaysDifference(startDate, endDate);

      datadogRum.addAction(`insights.channelSummary`, {
        range: daysDifference,
        filterType: InsightsMetricActionsType[filterType],
      });

      setLoading(false);
    },
    [service, filters, setChannelTracking, setChannelTotals, setMetricGraph, setFilters, setLoading]
  );

  const changeMetricChannel = useCallback(
    async (newMetric: ChannelTotalType) => {
      const metricId = InsightsMetricActionsId[newMetric];
      setMetricType(newMetric);
      await getData({ ...filters, filterType: metricId });
    },
    [filters, getData, setMetricType]
  );

  const updateFilters = useCallback(
    (options: ShippingFilters) => {
      void getData(options);
    },
    [getData]
  );

  return {
    events: {
      getData,
      changeMetricChannel,
      updateFilters,
    },
    state: {
      loading,
    },
  };
};
